// What the theme screens decide, as functions rather than inside components.
//
// NONE OF IT IS A RULE ABOUT THEMES. The order a show's theme is decided in is
// resolveShowTheme's, and the service answers it for every app on every read
// (`surfaces`). What is here is the console's half: which pointer a control
// writes, what a select shows as chosen, and what a sentence says about who
// wears what. Kept out of the components so check.mjs can say what each
// control sends by reading the request, and a reader can find the one place
// "missing key" and "own" are told apart.

import { HOUSE_THEME, OWN_SYSTEM, type ThemeSurfaceId } from "../../../ui/src/themeLibrary.js";
import { sameId, type LibraryRecord, type ShowThemes, type ThemeDraft } from "../api/themes.js";
import { surfaceLabel, WEARABLE_SURFACES } from "./parts.js";

export { OWN_SYSTEM };

/** The service's own cap on a name, restated for the copy suffix only. The service still decides. */
const NAME_MAX = 60;

/**
 * What a surface select's value is: "" follows the show, "own" is the app's
 * own system, anything else is a theme id. The three are the three answers
 * the service distinguishes, and the select has one option for each.
 */
export const FOLLOW = "";

export function pointerOf(show: ShowThemes, surface: ThemeSurfaceId): string {
  return show.surfaceThemes[surface] ?? FOLLOW;
}

/**
 * The pointers with one app's changed. FOLLOW removes the key, which is what
 * "follows the show" is on the wire; a key holding "" would be refused.
 */
export function withPointer(
  show: ShowThemes,
  surface: ThemeSurfaceId,
  value: string,
): Partial<Record<ThemeSurfaceId, string>> {
  const next: Partial<Record<ThemeSurfaceId, string>> = { ...show.surfaceThemes };
  if (value === FOLLOW) delete next[surface];
  else next[surface] = value;
  return next;
}

/** What following the show means right now: its event theme, else its client's default, else nothing. */
export function followedThemeId(show: ShowThemes): string | null {
  return show.showThemeId ?? show.clientDefaultThemeId;
}

/**
 * Whether an app at this show resolves to `themeId`. Read off the service's
 * own answer for that app rather than worked out again here.
 */
export function wears(show: ShowThemes, surface: ThemeSurfaceId, themeId: string): boolean {
  return sameId(show.surfaces.find((s) => s.surface === surface)?.themeId ?? null, themeId);
}

/** The labels of this show's apps wearing `themeId`, in the rail's order. */
export function wearersOf(show: ShowThemes, themeId: string): readonly string[] {
  return WEARABLE_SURFACES.filter(([id]) => wears(show, id, themeId)).map(([, label]) => label);
}

/**
 * The pointers after a "Where it is worn" switch is flipped.
 *
 * ON points the app at this theme. OFF has to leave the app NOT wearing it,
 * which is not always the same as removing the pointer: an app that wore this
 * because the show does would go on wearing it with no pointer at all. So off
 * removes a pointer at this theme, and if the app would then still follow its
 * way back here it is put on its own system instead. A switch that read off
 * and stayed on is the one outcome this must not have.
 */
export function flipWear(
  show: ShowThemes,
  surface: ThemeSurfaceId,
  themeId: string,
  on: boolean,
): Partial<Record<ThemeSurfaceId, string>> {
  if (on) return withPointer(show, surface, themeId.toLowerCase());
  const pointer = show.surfaceThemes[surface];
  if (pointer !== undefined && sameId(pointer, themeId)) {
    const followed = followedThemeId(show);
    return withPointer(show, surface, sameId(followed, themeId) ? OWN_SYSTEM : FOLLOW);
  }
  return withPointer(show, surface, OWN_SYSTEM);
}

export function nameOf(themes: readonly LibraryRecord[], themeId: string | null): string | null {
  if (themeId === null) return null;
  return themes.find((t) => sameId(t.themeId, themeId))?.name ?? null;
}

/** The library as a select offers it: everything that can be pointed at, which is everything but Nova house. */
export function pointable(themes: readonly LibraryRecord[]): readonly LibraryRecord[] {
  return themes.filter((t) => !t.locked && t.themeId !== HOUSE_THEME.id);
}

/**
 * A select's value for an id, spelt as the option is.
 *
 * The option values are the list's own ids; a pointer saved in capitals, or a
 * route parameter typed that way, would match none of them and the select
 * would quietly draw its first option as chosen. So the value is looked up in
 * the list without regard to case and drawn in the list's spelling.
 */
export function optionValue(themes: readonly LibraryRecord[], value: string): string {
  if (value === FOLLOW || value === OWN_SYSTEM) return value;
  return themes.find((t) => sameId(t.themeId, value))?.themeId ?? value;
}

export function draftOf(theme: LibraryRecord): ThemeDraft {
  return {
    name: theme.name,
    light: theme.light,
    dark: theme.dark,
    headFont: theme.headFont,
    bodyFont: theme.bodyFont,
    radius: theme.radius,
    logos: theme.logos,
  };
}

/** A copy: the same palette under a new name, worn by nothing until somebody points at it. */
export function copyOf(theme: LibraryRecord): ThemeDraft {
  const suffix = " copy";
  return { ...draftOf(theme), name: theme.name.slice(0, NAME_MAX - suffix.length) + suffix };
}

/** A new theme starts from Nova house, which passes the floor, so the first save is a real one. */
export function blankTheme(name: string): ThemeDraft {
  return {
    name: name.slice(0, NAME_MAX),
    light: HOUSE_THEME.light,
    dark: HOUSE_THEME.dark,
    headFont: HOUSE_THEME.headFont,
    bodyFont: HOUSE_THEME.bodyFont,
    radius: HOUSE_THEME.radius,
    logos: null,
  };
}

/** "Registration, Wheel and Kiosk". */
export function listed(labels: readonly string[]): string {
  if (labels.length <= 1) return labels.join("");
  return `${labels.slice(0, -1).join(", ")} and ${labels[labels.length - 1]}`;
}

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"] as const;

/**
 * The day a theme was last revised, as the card prints it: "17 Sep 2026".
 * Spelt out rather than left to toLocaleDateString, whose en-GB short month
 * is "Sept" in some engines and "Sep" in others.
 */
export function editedOn(revisedAt: string | null): string | null {
  if (revisedAt === null) return null;
  const at = new Date(revisedAt);
  if (Number.isNaN(at.getTime())) return null;
  return `${at.getUTCDate()} ${MONTHS[at.getUTCMonth()] ?? ""} ${at.getUTCFullYear()}`;
}

export { surfaceLabel };
